// Motion presets — single source of truth for framer-motion timings.
//
// Rule (binding): animations fire on state change only. Nothing pulses
// on idle. All transitions ≤240ms unless decorative-by-design (the
// urge-surf wave is the only exception). prefers-reduced-motion is
// honored by re-exporting framer's useReducedMotion and short-circuiting
// presets when true.

import { useEffect, useRef, useState } from 'react';
import { cubicBezier, useReducedMotion } from 'framer-motion';

// Pre-baked easing functions. Using cubicBezier() (rather than the
// raw 4-number tuple) keeps TypeScript happy across framer-motion 12+,
// where the `ease` prop's Easing[] union otherwise mis-narrows.
export const EASE_OUT = cubicBezier(0.2, 0.8, 0.2, 1);
export const EASE_EMPHASIS = cubicBezier(0.16, 1, 0.3, 1);

// useTickUp — animate a numeric value from its previous value to the
// current value over `durationMs`. Returns the animated number to render.
//
// When prefers-reduced-motion is set, returns the target instantly.
//
// Math: linear ease for monotonic counters (a streak going up reads
// linearly to the eye). Use ease-emphasis for money/hours which want a
// soft settle.
export function useTickUp(
  target: number,
  options: { durationMs?: number; ease?: 'linear' | 'emphasis' } = {},
): number {
  const { durationMs = 600, ease = 'linear' } = options;
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef(target);

  useEffect(() => {
    targetRef.current = target;
    if (reduce) {
      setDisplay(target);
      fromRef.current = target;
      return;
    }
    if (target === display) return;
    fromRef.current = display;
    startRef.current = null;

    const step = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = ease === 'emphasis' ? emphasisEase(t) : t;
      const next = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplay(targetRef.current);
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, reduce]);

  return display;
}

function emphasisEase(t: number) {
  // Match TRANSITION_EMPHASIS ease curve approximately.
  return 1 - Math.pow(1 - t, 3);
}

// usePrev — small helper so callers can pulse a container when a value
// increments.
export function usePrev<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export { useReducedMotion };
